import { ControlledDateField, ControlledTextField, ControlNumberField } from 'components/Form';
import { FIELD_WIDTH } from 'constantsProject';
import { ProjectStatusEnum } from 'gql/graphql';
import { Grid, GridItem, CollapseGroup } from 'uikit';
import { getLengthValidator } from 'utils';
import { replacerCost } from 'utils/replacerCost';

import type React from 'react';
import { useEffect, useMemo } from 'react';
import type { Control, UseFormWatch } from 'react-hook-form';
import type { UseFormSetValue } from 'react-hook-form/dist/types/form';

import type { TDisabledByStatusMap } from '../hooks/useIsDisabledByStatus';
import { MayBeDisabledByStatusKeys } from '../hooks/useIsDisabledByStatus';

interface IBimContractEditCommonForm {
    control: Control;
    classNameTooltip?: string;
    watch: UseFormWatch<any>;
    setValue: UseFormSetValue<any>;
    hasWorkOrders: boolean;
    isDisabledByStatusMap: TDisabledByStatusMap;
    onTooltipAttributeChange?: (attributeName: string, target: Node) => void;
    isDisabledRole: boolean;
    statusProject?: ProjectStatusEnum;
}

export const bimContractCommonCollapseStyle = {
    width: FIELD_WIDTH,
    margin: 'var(--space-margin-bottom)',
    marginLeft: 0,
};

export const BimContractEditCommonForm: React.FC<IBimContractEditCommonForm> = (props) => {
    const {
        control,
        classNameTooltip,
        isDisabledByStatusMap,
        watch,
        setValue,
        hasWorkOrders,
        onTooltipAttributeChange,
        isDisabledRole,
        statusProject,
    } = props;

    const isProjectStatusDelayed = statusProject === ProjectStatusEnum.Delayed;

    useEffect(() => {
        const subscription = watch((value, { name, type }) => {
            if (name === 'generalInfo.amountWithoutTax' && type === 'change') {
                const amountWithoutTax = value?.generalInfo?.amountWithoutTax;

                const amountWithoutTaxNum = amountWithoutTax && replacerCost(amountWithoutTax);
                const tax = amountWithoutTaxNum * 0.2;

                setValue('generalInfo.tax', tax);
                setValue('generalInfo.amountWithTax', tax + amountWithoutTaxNum);
            }
        });

        return () => subscription.unsubscribe();
    }, [setValue, watch]);

    const commonGroups = useMemo(
        () => [
            {
                label: 'Данные договора',
                content: (
                    <Grid rowGap="s" cols="1">
                        <GridItem>
                            <ControlledTextField
                                name={MayBeDisabledByStatusKeys.generalInfoSubject}
                                label="Предмет"
                                control={control}
                                type="text"
                                required
                                rules={getLengthValidator(true)}
                                classNameTooltip={classNameTooltip}
                                disabled={
                                    isDisabledByStatusMap[MayBeDisabledByStatusKeys.generalInfoSubject] ||
                                    hasWorkOrders ||
                                    isDisabledRole ||
                                    isProjectStatusDelayed
                                }
                                onTooltipAttributeChange={onTooltipAttributeChange}
                                placeholder="Предмет"
                            />
                        </GridItem>
                        <GridItem>
                            <ControlledTextField
                                name={MayBeDisabledByStatusKeys.generalInfoInternalNumber}
                                label="Внутренний номер"
                                control={control}
                                type="text"
                                required
                                rules={getLengthValidator(true)}
                                classNameTooltip={classNameTooltip}
                                disabled={
                                    isDisabledByStatusMap[MayBeDisabledByStatusKeys.generalInfoInternalNumber] ||
                                    hasWorkOrders ||
                                    isDisabledRole ||
                                    isProjectStatusDelayed
                                }
                                onTooltipAttributeChange={onTooltipAttributeChange}
                                placeholder="1"
                            />
                        </GridItem>
                        <GridItem>
                            <ControlledTextField
                                name="generalInfo.customer"
                                label="Заказчик"
                                control={control}
                                type="text"
                                disabled
                            />
                        </GridItem>
                        <GridItem>
                            <ControlledTextField
                                name={MayBeDisabledByStatusKeys.generalInfoCustomerAddress}
                                label="Адрес заказчика"
                                control={control}
                                type="text"
                                disabled={
                                    isDisabledByStatusMap[MayBeDisabledByStatusKeys.generalInfoCustomerAddress] ||
                                    hasWorkOrders ||
                                    isDisabledRole ||
                                    isProjectStatusDelayed
                                }
                                rules={getLengthValidator(false)}
                                classNameTooltip={classNameTooltip}
                                onTooltipAttributeChange={onTooltipAttributeChange}
                                placeholder="Адрес заказчика"
                            />
                        </GridItem>
                        <GridItem>
                            <ControlledTextField
                                name={MayBeDisabledByStatusKeys.generalInfoContractor}
                                label="Подрядчик"
                                control={control}
                                type="text"
                                required
                                rules={getLengthValidator(true)}
                                classNameTooltip={classNameTooltip}
                                disabled={
                                    isDisabledByStatusMap[MayBeDisabledByStatusKeys.generalInfoContractor] ||
                                    hasWorkOrders ||
                                    isDisabledRole ||
                                    isProjectStatusDelayed
                                }
                                onTooltipAttributeChange={onTooltipAttributeChange}
                                placeholder="Подрядчик"
                            />
                        </GridItem>
                        <GridItem>
                            <ControlledTextField
                                name={MayBeDisabledByStatusKeys.generalInfoContractorAddress}
                                label="Адрес подрядчика"
                                control={control}
                                type="text"
                                disabled={
                                    isDisabledByStatusMap[MayBeDisabledByStatusKeys.generalInfoContractorAddress] ||
                                    isDisabledRole ||
                                    isProjectStatusDelayed
                                }
                                rules={getLengthValidator(false)}
                                classNameTooltip={classNameTooltip}
                                onTooltipAttributeChange={onTooltipAttributeChange}
                                placeholder="Адрес подрядчика"
                            />
                        </GridItem>
                    </Grid>
                ),
            },
            {
                label: 'Даты договора',
                content: (
                    <Grid rowGap="s" cols="1">
                        <GridItem>
                            <ControlledDateField
                                name={MayBeDisabledByStatusKeys.generalInfoSigningDate}
                                label="Подписание"
                                control={control}
                                type="date"
                                disabled={
                                    isDisabledByStatusMap[MayBeDisabledByStatusKeys.generalInfoSigningDate] ||
                                    hasWorkOrders ||
                                    isDisabledRole ||
                                    isProjectStatusDelayed
                                }
                            />
                        </GridItem>
                        <GridItem>
                            <ControlledDateField
                                name={MayBeDisabledByStatusKeys.generalInfoStartDate}
                                label="Начало действия"
                                control={control}
                                type="date"
                                disabled={
                                    isDisabledByStatusMap[MayBeDisabledByStatusKeys.generalInfoStartDate] ||
                                    hasWorkOrders ||
                                    isDisabledRole ||
                                    isProjectStatusDelayed
                                }
                            />
                        </GridItem>
                        <GridItem>
                            <ControlledDateField
                                name={MayBeDisabledByStatusKeys.generalInfoFinishDate}
                                label="Окончание действия"
                                control={control}
                                type="date"
                                disabled={
                                    isDisabledByStatusMap[MayBeDisabledByStatusKeys.generalInfoFinishDate] ||
                                    hasWorkOrders ||
                                    isDisabledRole ||
                                    isProjectStatusDelayed
                                }
                            />
                        </GridItem>
                    </Grid>
                ),
            },
            {
                label: 'Сумма договора',
                content: (
                    <Grid rowGap="s" cols="1">
                        <GridItem>
                            <ControlNumberField
                                name="cost.contractPlanWithTax"
                                label="Сумма с НДС (руб.)"
                                control={control}
                                type="number"
                                disabled
                                labelPosition="left"
                                width="full"
                                size="s"
                                placeholder="Сумма с НДС"
                            />
                        </GridItem>
                        <GridItem>
                            <ControlNumberField
                                name={MayBeDisabledByStatusKeys.generalInfoAmountWithoutTax}
                                label="Сумма без НДС (руб.)"
                                control={control}
                                type="number"
                                labelPosition="left"
                                width="full"
                                size="s"
                                disabled
                                placeholder="Сумма без НДС"
                            />
                        </GridItem>
                        <GridItem>
                            <ControlNumberField
                                name="cost.contractPlanTax"
                                label="НДС, 20% (руб.)"
                                control={control}
                                type="number"
                                disabled
                                labelPosition="left"
                                width="full"
                                size="s"
                                placeholder="НДС"
                            />
                        </GridItem>
                    </Grid>
                ),
            },
            {
                label: 'Дополнительно',
                content: (
                    <Grid rowGap="s" cols="1">
                        <GridItem>
                            <ControlledTextField
                                name={MayBeDisabledByStatusKeys.generalInfoNote}
                                label="Примечание"
                                control={control}
                                type="textarea"
                                cols={50}
                                rows={5}
                                rules={{
                                    maxLength: {
                                        value: 255,
                                        message: 'не более 255 символов',
                                    },
                                }}
                                disabled={
                                    isDisabledByStatusMap[MayBeDisabledByStatusKeys.generalInfoNote] ||
                                    isDisabledRole ||
                                    isProjectStatusDelayed
                                }
                                classNameTooltip={classNameTooltip}
                                onTooltipAttributeChange={onTooltipAttributeChange}
                                placeholder="Примечание"
                            />
                        </GridItem>
                    </Grid>
                ),
            },
        ],
        [
            classNameTooltip,
            control,
            hasWorkOrders,
            isDisabledByStatusMap,
            isDisabledRole,
            isProjectStatusDelayed,
            onTooltipAttributeChange,
        ],
    );

    const opened = useMemo(() => commonGroups.map((_, index) => index), [commonGroups]);

    return <CollapseGroup items={commonGroups} style={bimContractCommonCollapseStyle} opened={opened} />;
};

BimContractEditCommonForm.displayName = 'BimContractEditCommonForm';
